import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, DAY_NAMES, inboxApi, type WorkingHour } from '@wa/shared';

interface DayDraft {
  open: boolean;
  opensAt: string;
  closesAt: string;
}

const CLOSED: DayDraft = { open: false, opensAt: '10:00', closesAt: '19:00' };

/**
 * Working hours and the away message.
 *
 * One window a day, edited as a row per weekday. The table can hold two — a lunch
 * break — and the API accepts them; this screen does not offer it yet, because a
 * repeating row builder is a lot of interface for something no client has asked
 * for, and the shape it would need is different from this one. A schedule with two
 * windows on a day saved elsewhere is shown as its first window here, which is the
 * honest limit and the reason it is written down rather than hidden.
 *
 * Times are the tenant's own wall clock, which for every account on this platform
 * is IST. That is the one place in the product where a time is not stored in UTC:
 * "we open at ten" is a statement about a clock on a wall, not about an instant.
 */
export function HoursPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const settings = useQuery({ queryKey: ['settings'], queryFn: inboxApi.settings });
  const hours = useQuery({ queryKey: ['working-hours'], queryFn: inboxApi.workingHours });

  const [enabled, setEnabled] = useState(false);
  const [hold, setHold] = useState(true);
  const [message, setMessage] = useState('');
  const [opener, setOpener] = useState<number | null>(null);
  const [days, setDays] = useState<DayDraft[]>(() => DAY_NAMES.map(() => ({ ...CLOSED })));

  // Seeded from the server once it answers, and only then. Initialising from an
  // undefined query and correcting later makes the form flicker from "closed all
  // week" into the real schedule, which reads as the settings having been lost.
  useEffect(() => {
    if (settings.data) {
      setEnabled(settings.data.hoursEnabled);
      setHold(settings.data.holdAssignment);
      setMessage(settings.data.awayMessage ?? '');
      setOpener(settings.data.sessionOpenerTemplateId);
    }
  }, [settings.data]);

  useEffect(() => {
    if (hours.data) setDays(toDrafts(hours.data));
  }, [hours.data]);

  const fail = (e: unknown, fallback: string) => { setSaved(false); setError(e instanceof ApiError ? e.message : fallback); };

  const save = useMutation({
    mutationFn: async () => {
      // The schedule first. Switching hours on with nothing behind it would mean a
      // moment where the account is closed all week.
      await inboxApi.setWorkingHours(null, days.flatMap((day, index) =>
        day.open ? [{ dayOfWeek: index, opensAt: day.opensAt, closesAt: day.closesAt }] : []));

      await inboxApi.updateSettings({
        hoursEnabled: enabled,
        holdAssignment: hold,
        awayMessage: message.trim() || null,
        // -1 clears it. COALESCE on the server cannot express "set to null", and a
        // separate route for one nullable column is a route to forget.
        sessionOpenerTemplateId: opener ?? -1,
      });
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['working-hours'] });
    },
    onError: (e) => fail(e, 'Could not save your hours.'),
  });

  const noWindows = days.every((d) => !d.open);
  const blocked = enabled && (message.trim().length === 0 || noWindows);

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {saved && !save.isPending && (
        <p role="status" className="rounded border border-(--color-good) px-3 py-2 text-small text-(--color-good)">
          Saved.
        </p>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          When you are open
        </h2>

        <div className="flex flex-col gap-3 px-3 py-3 sm:px-4">
          <label className="flex items-start gap-2 text-small">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="mt-0.5" />
            <span>
              Use working hours
              <span className="block text-micro text-(--color-muted)">
                Outside them, customers get your away message once per closed period &mdash; not once
                per message, and not only the first time.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-small">
            <input
              type="checkbox"
              checked={hold}
              disabled={!enabled}
              onChange={(e) => setHold(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Hold assignment until you open
              <span className="block text-micro text-(--color-muted)">
                An assigned conversation leaves the Unassigned queue, so routing one to somebody
                who left at seven hides it from everyone who could answer.
              </span>
            </span>
          </label>

          <ul className="flex flex-col divide-y divide-(--color-line-dim)">
            {/* Mapped over the drafts, not over DAY_NAMES: indexing the draft array
                by a name's position gives TypeScript a possibly-undefined element on
                every read, and seven of them in one row is a lot of non-null
                assertions to hide one off-by-one behind. */}
            {days.map((day, index) => (
              <li key={DAY_NAMES[index]} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
                <label className="flex min-w-32 flex-1 basis-32 items-center gap-2 text-small">
                  <input
                    type="checkbox"
                    checked={day.open}
                    disabled={!enabled}
                    onChange={(e) => setDays((d) => d.map((v, i) => i === index ? { ...v, open: e.target.checked } : v))}
                  />
                  <span className="truncate">{DAY_NAMES[index]}</span>
                </label>

                {day.open ? (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <input
                      type="time"
                      value={day.opensAt}
                      disabled={!enabled}
                      aria-label={`${DAY_NAMES[index]} opens at`}
                      onChange={(e) => setDays((d) => d.map((v, i) => i === index ? { ...v, opensAt: e.target.value } : v))}
                      className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1 text-small"
                    />
                    <span className="text-small text-(--color-muted)">to</span>
                    <input
                      type="time"
                      value={day.closesAt}
                      disabled={!enabled}
                      aria-label={`${DAY_NAMES[index]} closes at`}
                      onChange={(e) => setDays((d) => d.map((v, i) => i === index ? { ...v, closesAt: e.target.value } : v))}
                      className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1 text-small"
                    />
                  </div>
                ) : (
                  <span className="shrink-0 text-small text-(--color-muted)">Closed</span>
                )}
              </li>
            ))}
          </ul>

          <label className="flex flex-col gap-1">
            <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Away message</span>
            <textarea
              value={message}
              rows={3}
              disabled={!enabled}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Thanks for writing. We are closed right now and will reply when we open at 10am."
              className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
            />
            <span className="text-micro text-(--color-muted)">
              Sent only inside WhatsApp&rsquo;s 24-hour window. Somebody writing days after their
              last message gets nothing rather than a message that fails.
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={blocked || save.isPending}
              onClick={() => save.mutate()}
              className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save hours'}
            </button>

            {blocked && (
              <p className="text-micro text-(--color-muted)">
                {noWindows
                  ? 'Open on at least one day, or turn working hours off.'
                  : 'Write the message customers should get before switching hours on.'}
              </p>
            )}

            <p className="w-full text-micro text-(--color-muted)">
              Times are {settings.data?.timeZone ?? 'Asia/Kolkata'} &mdash; your own clock, not UTC.
            </p>
          </div>
        </div>
      </section>

      <SessionOpenerSetting value={opener} onChange={setOpener} />
    </div>
  );
}

/**
 * Which template the inbox uses to reopen a conversation past the 24-hour window.
 *
 * Without this the feature exists and cannot be switched on, which is the shape of
 * dead code: the column, the endpoint and the composer were all built and the only
 * way to nominate a template was a SQL update.
 *
 * Only approved templates with exactly one body variable are offered. Zero has no
 * gap for the agent to type into; two would need a form, at which point it is the
 * template picker, which already exists and is better at it.
 */
function SessionOpenerSetting({ value, onChange }: {
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const templates = useQuery({ queryKey: ['templates'], queryFn: inboxApi.templates, staleTime: 300_000 });

  const usable = (templates.data ?? []).filter(
    (t) => t.status === 'APPROVED' && t.bodyVariables === 1);
  const chosen = usable.find((t) => t.id === value);

  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
      <div className="border-b border-(--color-line) px-4 py-3">
        <h3 className="text-body font-semibold tracking-tight">Reopening a closed chat</h3>
        <p className="mt-1 text-small text-(--color-muted)">
          After 24 hours of quiet WhatsApp will not carry an ordinary message, only an
          approved template. Nominate one here and the inbox turns it into a message
          box: the agent types the middle and the template goes out.
        </p>
      </div>

      <div className="flex flex-col gap-3 px-4 py-4">
        <label className="block">
          <span className="mb-1.5 block text-small text-(--color-muted)">Template</span>
          <select
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            className="h-10 w-full max-w-md rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 text-body outline-none focus:border-(--color-brand)"
          >
            <option value="">None &mdash; agents pick a template each time</option>
            {usable.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
            ))}
          </select>
        </label>

        {!templates.isPending && usable.length === 0 && (
          <p className="text-small text-(--color-muted)">
            No approved template takes exactly one variable yet. Create a utility
            template whose body has a single <code>{'{{1}}'}</code> in it, and it will
            appear here once Meta approves it.
          </p>
        )}

        {chosen && (
          <div className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) p-3">
            <p className="mb-1 text-micro font-medium tracking-wide text-(--color-muted) uppercase">
              What an agent will see
            </p>
            <p className="text-small whitespace-pre-wrap">
              {(chosen.bodyText ?? '').replace(/\{\{\s*1\s*\}\}/, '  [ the agent types here ]  ')}
            </p>
          </div>
        )}

        {/* Said here rather than only in the inbox. Whoever nominates the template
            is deciding that agents may open billed conversations with one click, and
            they are the person who should read this — a utility template used to
            start conversations customers did not ask for is the usual reason a
            number's quality rating is cut and then its daily limit. */}
        <p className="max-w-prose text-micro text-(--color-faint)">
          Every send through this box is a paid conversation, and the template must
          genuinely be a reply somebody is expecting. Utility templates used to start
          conversations customers did not ask for are the usual reason Meta cuts a
          number&rsquo;s quality rating and then its daily sending limit.
        </p>

        <p className="text-micro text-(--color-muted)">
          Saved with the working hours above.
        </p>
      </div>
    </section>
  );
}

/**
 * Server rows to one row per weekday.
 *
 * Only the tenant-wide schedule: a per-channel override is stored and honoured by
 * the server, but this screen edits the default. Two windows on one day collapse to
 * the earliest, which is the limitation the component's own comment states.
 */
function toDrafts(rows: WorkingHour[]): DayDraft[] {
  return DAY_NAMES.map((_, index) => {
    const forDay = rows
      .filter((r) => r.channelId === null && r.dayOfWeek === index)
      .sort((a, b) => a.opensAt.localeCompare(b.opensAt));

    const first = forDay[0];
    return first === undefined
      ? { ...CLOSED }
      : { open: true, opensAt: first.opensAt, closesAt: first.closesAt };
  });
}
